import { createAction } from '@reduxjs/toolkit';
import moment from 'moment';
import { pathOr } from 'ramda';
import { getClientContext } from 'utils/clientContextUtil';
import {
  INFORM_CHOICE,
  DOCUMENTS_ON_WAY,
  CONFIRM_JOINT_LINK_CODE,
  YOUR_MAIN_BANK_ACCOUNT,
  INELIGIBILTY_SCREEN,
  DIP_UNLIKELY_RESULT,
  DIP_NO_MATCH,
  DIP_GREAT_NEWS,
  DIP_REVIEW_DETAILS,
  WARNING_MESSAGE_SCREEN,
  DIP_ABOUT_THIS_CHECK,
  CHOOSE_A_USERNAME,
  YOUR_LOAN_OPTIONS,
  SOLE_INITIAL_QUESTIONS,
  DIP_YOUR_LOAN_OPTIONS,
  DIP_INFORMATION_ABOUT_YOUR_LOAN,
  NTB_DIP_KEY_FEATURES
} from 'constants/paths';
import { RESUME_LOAN_TYPE, UK_ONLY_CITIZENSHIP, YES_OPTION } from 'constants/general';
import { formatShortenedIsoDate, padZero } from 'utils/genericUtil';
import { nextRouteTask } from 'utils/getRoutePath';
import { responseFailed } from './error';
import { redirectCustomerDetails } from './navigation';
import { showHideLoader } from './loader';
import { getAccountList } from './account';
import { getCompleteCase } from './complete';
import { setUserJourney, setJointJourney, setSecondaryApplicant } from './config';
import {
  NTB_JOURNEY,
  EXISTING_JOURNEY,
  PRE_ASSESSED_JOURNEY,
  DEBT_CONSOLIDATION,
  YES_VALUE,
  LOAN_TYPE,
  CREDIT_CARD_TYPE,
  CURRENT_TYPE,
  EXTERNAL_TYPE,
  JOINT_TYPE,
  TASK_CREDIT_DECLINE,
  TASK_ACCOUNT_OPENED,
  ORIGIN_IB,
  TASK_APPLICATION_CANCELLED,
  TASK_APPLICATION_EXPIRED,
  STATUS_CODE_ACCEPTED,
  A_SECOND_IN_MS,
  UNABLE_LONGER_TERM,
  UNLIKELY,
  NO_MATCH,
  REALLY_GOOD,
  PRE_APPROVED,
  GOOD,
  UNABLE,
  EMPLOYMENT_STATUSES
} from 'constants/general';
import {
  INCORRECT_LINK_CODE_ERROR,
  CASE_ALREADY_EXIST_CODE_745,
  CASE_ALREADY_EXIST_CODE_746
} from 'constants/errorCode';
import { getDeviceContext } from 'utils/deviceContext';
import { navigateToScreen } from 'actionCreators/navigation';
import { getTotalDebtAmount } from 'utils/getTotalDebtDIP';
import { takeScreenShot } from 'utils/screenshotUtil';
import { push } from 'connected-react-router';
import { addEmploymentDetails } from 'actionCreators/customer';
import { getBlackbox } from 'utils/DeviceRisk';
import { CREDIT_DECISION_ROUTES_MAP } from 'components/screens/AboutThisCheckDIP/constants';
import { getValidCurrentAccounts } from 'utils/accountUtil';

export const createCase = createAction('case/create', function prepare(config, caseDetails) {
  return {
    payload: {
      endPoint: 'banks/:bankId/cases/personal-loan',
      method: 'post',
      body: createCaseRequest({ config, isLinkCodeRequired: true, caseDetails }),
      isPaasApi: true,
      addHeader: true,
      success: createCaseSuccess,
      failure: createCaseFailed,
      version: '1.2.0',
      useEnvTag: true
    }
  };
});

export const createAndUpdateCase = createAction('case/createAndUpdate', function prepare(config) {
  return {
    payload: {
      endPoint: 'banks/:bankId/cases/personal-loan',
      method: 'post',
      body: createCaseRequest({ config, isLinkCodeRequired: true }),
      isPaasApi: true,
      addHeader: true,
      success: createAndUpdateCaseSuccessThunk,
      failure: createCaseFailed,
      version: '1.2.0',
      useEnvTag: true
    }
  };
});

const createCaseRequest = ({
  config,
  isLinkCodeRequired = false,
  includeSourceId = true,
  caseDetails
}) => {
  const { customerId, jointLinkCode, journey, isJoint = false, sourceId } = config;
  const { isNative } = getDeviceContext();
  const source_id = isNative ? 'APP' : sourceId;
  const existingDipCaseId = caseDetails?.dipCaseInfo?.submitDipCaseResponse?.dipCaseId;
  return {
    customer_id: customerId,
    ...(source_id && includeSourceId && { source_id }),
    ...(existingDipCaseId && { dip_case_id: existingDipCaseId }),
    case_subtype: journey !== NTB_JOURNEY ? EXISTING_JOURNEY : NTB_JOURNEY,
    product: {
      type: 'PL',
      sub_type: isJoint ? JOINT_TYPE.toUpperCase() : journey,
      variant: ''
    },
    ...(isLinkCodeRequired && jointLinkCode && { joint_link_code: jointLinkCode })
  };
};

export const createCaseSuccess = createAction('case/createCaseSuccess', function prepare(response) {
  return {
    payload: response
  };
});

export const createAndUpdateCaseSuccess = createAction(
  'case/createCaseSuccess',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const createAndUpdateCaseSuccessThunk = (successResponse) => (dispatch, getState) => {
  dispatch(createAndUpdateCaseSuccess(successResponse));

  const {
    config,
    loanValues,
    form: formState,
    caseDetails,
    customer: { customerInfo, newIncome }
  } = getState();

  dispatch(
    updateAndGetCase(
      config,
      loanValues,
      formState,
      caseDetails,
      customerInfo,
      caseDetails.caseId,
      null,
      false,
      null,
      null,
      newIncome
    )
  );
};

export const createCaseFailed = (failedResponse) => (dispatch) => {
  // fail response is coming in below format from apiUrlmiddleware

  if (failedResponse.error) {
    const {
      error: { originalError: { response: { data } } = { response: { data } } } = {
        originalError: { response: { data: {} } }
      }
    } = failedResponse;

    if (
      data.error &&
      (data.error.code === INCORRECT_LINK_CODE_ERROR ||
        data.error.code === CASE_ALREADY_EXIST_CODE_745 ||
        data.error.code === CASE_ALREADY_EXIST_CODE_746)
    ) {
      const { code } = data.error;
      dispatch(showHideLoader(false));
      dispatch(createCaseFailedResponse(data));
      dispatch(
        navigateToScreen(
          code === INCORRECT_LINK_CODE_ERROR ? CONFIRM_JOINT_LINK_CODE : INELIGIBILTY_SCREEN
        )
      );
    } else {
      dispatch(responseFailed(data));
    }
  } else {
    /**
     * A non-network response related error may have been passed through.
     */
    if (failedResponse instanceof Error) {
      if (window.dtrum) {
        window.dtrum.reportError(failedResponse);
      }
      dispatch(responseFailed({ code: '', message: failedResponse.message }));
    } else {
      /** Some kind of unexpected object has managed to reach us */
      const message = `Unparseable object recieved in-error: ${JSON.stringify(failedResponse)}`;
      if (window.dtrum) {
        window.dtrum.reportError(new Error(message));
      }
      dispatch(
        responseFailed({
          code: '',
          message: message
        })
      );
    }
  }
};

export const createCaseFailedResponse = createAction(
  'case/createCaseFailedResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const updateDIPAttemptCount = createAction(
  'dip-case/update-dip-attempt-count',
  function prepare(count) {
    return {
      payload: count
    };
  }
);

export const createDIPCase = () => (dispatch, getState) => {
  const { config, customer, form, caseDetails, account, loanValues } = getState();

  dispatch({
    type: 'dip-case/create',
    payload: {
      endPoint: 'banks/:bankId/cases/personal-loan/dip',
      method: 'post',
      body: createDIPCaseRequest({
        config,
        customer,
        form,
        caseDetails,
        account,
        loanValues
      }),
      isPaasApi: true,
      addHeader: true,
      success: createDIPCaseSuccessThunk,
      successParam: { form, caseDetails },
      failure: createDIPCaseFailed,
      version: '1.2.0',
      useEnvTag: true
    }
  });
};

const getDIPInternalDebt = (form, account) => {
  const { InternalDebtConsolidationForm } = form;
  if (InternalDebtConsolidationForm) {
    const { values } = InternalDebtConsolidationForm;
    const { accountDetailList } = account;
    const request = {
      total_internal_debt: getTotalDebtAmount(InternalDebtConsolidationForm, accountDetailList),
      debt_details: []
    };
    // iterate the values object to create DebtDetails object for SubmitDIP
    Object.entries(values).forEach(([key, value]) => {
      const foundAccount = accountDetailList.find((account) => account.id === key);
      if (foundAccount && value && value !== -1) {
        if (foundAccount.type === LOAN_TYPE || foundAccount.type === CURRENT_TYPE) {
          request.debt_details.push({
            account_details: {
              account_id: foundAccount.id,
              account_number: foundAccount.number.split('-')[1],
              sort_code: foundAccount.number.split('-')[0]
            },
            amount: Math.abs(value),
            type: foundAccount.type
          });
        } else if (foundAccount.type === CREDIT_CARD_TYPE) {
          request.debt_details.push({
            account_details: {
              account_id: foundAccount.id
            },
            amount: Math.abs(value),
            type: 'creditcard'
          });
        }
      } else if (key === EXTERNAL_TYPE && values.externalAmount && values.externalAmount !== -1) {
        request.debt_details.push({
          amount: parseFloat(values.externalAmount),
          type: EXTERNAL_TYPE
        });
      }
    });
    return request;
  }
};

const createDIPCaseRequest = ({ config, customer, form, caseDetails, account, loanValues }) => {
  const { bankId, userTrackingId, customerId, endUserIp, journey } = config;
  const {
    customerInfo: {
      customer_number,
      personal_details: { country_of_birth, nationality },
      employment_details_extra: { employment_industry, employment_job_level }
    },
    financialCircumstanceValue
  } = customer;
  let {
    PersonalDetailsDIPForm: {
      values: {
        name: { title, first_name, last_name, middle_name } = {},
        gender,
        dob,
        marital_status,
        number_of_dependents,
        yearMonthAccount: { months, years } = {},
        marital_status_other
      } = {}
    } = {},
    AddressDetailsDIPForm: {
      values: {
        ADDRESS_HISTORY,
        MOVE_IN_DATE: { month, year } = {},
        LIVING_ARRANGEMENT,
        otherCountry,
        anotherCountry,
        UKCitizenship
      } = {}
    } = {},
    EmploymentDetailsDIPForm: {
      values: {
        employersName,
        /**
         * @note this may seem unused, but is conditionally re-assigned later. If we get rid of this here,
         * we can't re-assign it later as it doesn't exist, causing a crash.
         * @todo if we really don't use this, also remove the re-assignment.
         */
        employmentStartDate,
        employmentStatus,
        otherEmploymentStatus,
        financialCircumstancesChange,
        monthlyIncome,
        mortgageExpense,
        occupation,
        paymentFrequency,
        annualIncomeBeforeTax,
        /**
         * @note this may seem unused, but is conditionally re-assigned later. If we get rid of this here,
         * we can't re-assign it later as it doesn't exist, causing a crash.
         * @todo if we really don't use this, also remove the re-assignment.
         */
        creditExpense,
        incomeLikelyGoDown,
        amountGoesInBankAccountPerMonth,
        earningsWhenIncomeDown,
        monthlyMortgagePayout,
        monthlyPersonalLoansPayout,
        outgoingsLikelyGoUp
      } = {}
    } = {}
  } = form;

  const { dipReprocessMoveInDate, dipCaseInfo, caseId, ntbToDipForm, caseInfo } = caseDetails;

  if (!form.PersonalDetailsDIPForm) {
    ({
      name: { title, first_name, last_name, middle_name },
      gender,
      dob,
      marital_status,
      number_of_dependents,
      yearMonthAccount: { months, years },
      marital_status_other
    } = ntbToDipForm.PersonalDetailsDIPForm.values);
  }

  if (!form.AddressDetailsDIPForm) {
    ({
      ADDRESS_HISTORY,
      MOVE_IN_DATE: { month, year },
      LIVING_ARRANGEMENT,
      otherCountry,
      anotherCountry,
      UKCitizenship
    } = ntbToDipForm.AddressDetailsDIPForm.values);
  }
  if (!form.EmploymentDetailsDIPForm) {
    ({
      employersName,
      employmentStartDate,
      employmentStatus,
      otherEmploymentStatus,
      financialCircumstancesChange,
      monthlyIncome,
      mortgageExpense,
      occupation,
      paymentFrequency,
      annualIncomeBeforeTax,
      creditExpense,
      incomeLikelyGoDown,
      amountGoesInBankAccountPerMonth,
      earningsWhenIncomeDown,
      monthlyMortgagePayout,
      monthlyPersonalLoansPayout,
      outgoingsLikelyGoUp
    } = ntbToDipForm.EmploymentDetailsDIPForm.values);
  }
  const existingDipCaseID = dipCaseInfo?.submitDipCaseResponse?.dipCaseId;
  const addExistingDIPCaseID = dipReprocessMoveInDate === `${month}${year}` && existingDipCaseID;
  const addressList = [];
  const derivedAddressToList = [];
  if (ADDRESS_HISTORY.length > 0) {
    ADDRESS_HISTORY.forEach((previousAddress, index) => {
      const { effective_period, international_address, uk_address } = previousAddress;
      derivedAddressToList.push(
        index === 0
          ? `${year}-${month}-01`
          : formatShortenedIsoDate(pathOr('', ['from'], effective_period))
      );
      if (effective_period) {
        const addressBlock = {
          address_type: index === 0 ? 'current_address' : 'previous_address',
          address_prefix: uk_address?.address_prefix || '',
          organisation_name: uk_address?.organisation_name || '',
          house_number: uk_address?.house_number || '',
          house_name: uk_address?.house_name || '',
          street_name: uk_address?.street_name || uk_address?.street || '',
          address_line_1:
            uk_address?.address_line_1 ||
            uk_address?.address_line1 ||
            international_address?.address_line1 ||
            '',
          address_line_2:
            uk_address?.address_line_2 ||
            uk_address?.address_line2 ||
            international_address?.address_line2 ||
            '',
          address_line_3:
            uk_address?.address_line_3 ||
            uk_address?.address_line3 ||
            international_address?.address_line3 ||
            '',
          address_line_4:
            uk_address?.address_line_4 ||
            uk_address?.address_line4 ||
            international_address?.address_line4 ||
            '',
          address_reference: uk_address?.address_reference || '',
          city: uk_address?.city || '',
          county: uk_address?.county || '',
          postal_code: uk_address?.postal_code || international_address?.postal_code || '',
          country: international_address?.country || 'United Kingdom',
          address_date_from:
            index === 0
              ? `${year}-${month}-01`
              : formatShortenedIsoDate(pathOr('', ['from'], effective_period)),
          address_date_to:
            index === 0 ? moment().format('YYYY-MM-DD') : derivedAddressToList[index - 1]
        };
        addressList.push(addressBlock);
      }
    });
  }
  const addDebtDetailsPayload =
    (form?.LoanOptionsForm?.values?.debtConsolidation &&
      form?.LoanOptionsForm?.values?.debtConsolidation === 'yes') ||
    form?.LoanOptionsForm?.values?.loanPurpose === 'Debt Consolidation';
  const otherCitizenshipValue = [];
  if (!otherCountry || otherCountry === '' || otherCountry === 'UK only') {
    otherCitizenshipValue.push('none');
  } else {
    otherCitizenshipValue.push(otherCountry);
  }

  if (anotherCountry && anotherCountry !== 'No') {
    otherCitizenshipValue.push(anotherCountry);
  }
  function getFinancialCircumstanceValue() {
    if (!financialCircumstanceValue) {
      return caseInfo?.financial_details?.change_of_circumstance?.income?.gross_annual
        ? true
        : false;
    } else {
      return financialCircumstanceValue === 'Yes' ? true : false;
    }
  }

  return {
    x_bpi_client_context: getClientContext(bankId, userTrackingId),
    ...(addExistingDIPCaseID && !caseId && { dip_case_id: existingDipCaseID }),
    application_type: 'sole',
    joint_applicant_present: false,
    product_code: 'PLDD',
    loan_details: {
      amount: loanValues?.amount ? loanValues.amount?.toString() : caseInfo?.loan?.amount,
      term: parseInt(loanValues?.term ? loanValues.term : caseInfo?.loan?.term) * 12,
      purpose: form?.LoanOptionsForm?.values?.loanPurpose || caseInfo?.loan?.purpose,
      partial_debt_consolidation_statement:
        !form?.LoanOptionsForm?.values?.debtConsolidation ||
        form?.LoanOptionsForm?.values?.debtConsolidation === 'no'
          ? form?.LoanOptionsForm?.values?.loanPurpose === 'Debt Consolidation'
            ? true
            : false
          : true,
      ...(addDebtDetailsPayload && { internal_debt: getDIPInternalDebt(form, account) })
    },
    applicants: {
      applicant: [
        {
          applicant_type: 'primary',
          customer_id: customerId,
          customer_number,
          personal_details: {
            name: {
              title: title,
              first_name,
              middle_name,
              last_name
            },
            sex: gender === 'Male' || gender === 'M' ? 'M' : 'F',
            dob: moment(dob, 'YYYYMMDD', true).format('YYYY-MM-DD'),
            marital_status:
              marital_status === 'OTHER' ? marital_status_other : marital_status || '',
            residential_status: LIVING_ARRANGEMENT,
            country_of_birth,
            uk_citizen: UKCitizenship.toUpperCase(),
            number_of_dependents:
              number_of_dependents === '3+' ? '3' : `${parseInt(number_of_dependents, 10)}`,
            countries_of_citizenship: otherCitizenshipValue,
            nationality
          },
          addresses: {
            address: addressList
          },
          employers: {
            employer: [
              {
                employer_type:
                  employmentStatus === 'OTHER' && otherEmploymentStatus !== 'SELF EMPLOYED'
                    ? ''
                    : 'current',
                employer_name:
                  employmentStatus === 'OTHER' && otherEmploymentStatus !== 'SELF EMPLOYED'
                    ? ''
                    : employersName,
                employment_from_date:
                  '2020-01-01' /**@todo remove this hard coded value once new score card is available. */,
                employment_to_date:
                  employmentStatus === 'OTHER' && otherEmploymentStatus !== 'SELF EMPLOYED'
                    ? ''
                    : moment().format('YYYY-MM-DD'),
                employment_status:
                  employmentStatus === 'OTHER' ? otherEmploymentStatus : employmentStatus,
                occupation:
                  employmentStatus === 'OTHER' && otherEmploymentStatus !== 'SELF EMPLOYED'
                    ? ''
                    : occupation,
                employment_industry: employment_industry,
                employment_job_level: employment_job_level
              }
            ]
          },
          financial_details: {
            income: {
              gross_annual: parseFloat(annualIncomeBeforeTax),
              regular: {
                net_amount: monthlyIncome ? parseFloat(monthlyIncome) : 0,
                input_type: journey === NTB_JOURNEY ? 'HOUSEHOLD INCOME' : 'direct debit',
                frequency: journey === NTB_JOURNEY ? 'ANNUAL' : paymentFrequency
              }
            },
            expenditure: {
              mortgage: {
                net_amount: parseFloat(mortgageExpense),
                frequency: 'monthly'
              },
              others: {
                net_amount: journey === NTB_JOURNEY ? parseFloat(0) : parseFloat(creditExpense), //TODO: this value has been removed for NTB journey, but the api has not been updated to remove this field
                frequency: 'monthly'
              }
            },
            bank_details: {
              time_at_bank_year: years.toString(),
              time_at_bank_month: months.toString()
            },
            circumstance_change_statement:
              journey === NTB_JOURNEY
                ? getFinancialCircumstanceValue()
                : financialCircumstancesChange === YES_OPTION
                ? true
                : false,
            income_going_down:
              journey === NTB_JOURNEY
                ? getFinancialCircumstanceValue()
                : incomeLikelyGoDown === YES_OPTION
                ? true
                : false,
            income_change_details:
              journey === NTB_JOURNEY && getFinancialCircumstanceValue()
                ? {
                    revised_gross_income: amountGoesInBankAccountPerMonth
                      ? amountGoesInBankAccountPerMonth?.toString()
                      : parseInt(
                          caseInfo?.financial_details?.change_of_circumstance?.income?.gross_annual
                        ).toString()
                  }
                : journey === EXISTING_JOURNEY && incomeLikelyGoDown === YES_OPTION
                ? {
                    revised_gross_income: earningsWhenIncomeDown,
                    revised_monthly_net_income: amountGoesInBankAccountPerMonth
                  }
                : {},
            expenses_going_up: outgoingsLikelyGoUp === YES_OPTION ? true : false,
            expense_change_details:
              outgoingsLikelyGoUp === YES_OPTION
                ? {
                    revised_accomodation_expense: monthlyMortgagePayout,
                    revised_monthly_credit_repayment: monthlyPersonalLoansPayout
                  }
                : {}
          }
        }
      ]
    },
    device_details: {
      blackbox: getBlackbox(),
      end_user_ip: endUserIp
    }
  };
};

const otherEmploymentValues = [
  'SELF EMPLOYED',
  'RETIRED',
  'STUDENT',
  'HOUSE PERSON',
  'LONG TERM DISABLED',
  'UNEMPLOYED'
];

export const mapFields = createAction('dip/mapFields', function prepare(mappedFields) {
  return {
    payload: mappedFields
  };
});

export const mapNTBToDIPFields = () => (dispatch, getState) => {
  const {
    customer: {
      customerInfo: { personal_details, financial_details, employment_details },
      newIncome
    }
  } = getState();

  const [current_employment_details] = employment_details;

  const moveInDates = () => {
    const [year, month] = personal_details.addresses[0].effective_period.from.split('-');
    return { year, month };
  };

  const mappedDipFields = {
    PersonalDetailsDIPForm: {
      values: {
        name: personal_details.name,
        gender: personal_details.sex,
        dob: personal_details.dob,
        marital_status: personal_details.marital_status,
        number_of_dependents: personal_details.number_of_dependents || '0',
        yearMonthAccount: { months: 0, years: 0 },
        marital_status_other: personal_details.marital_status_other
      }
    },
    AddressDetailsDIPForm: {
      values: {
        ADDRESS_HISTORY: personal_details.addresses,
        MOVE_IN_DATE: moveInDates(),
        LIVING_ARRANGEMENT: personal_details.residential_status,
        otherCountry: UK_ONLY_CITIZENSHIP.includes(
          financial_details.tax.countries_of_citizenship[0]
        )
          ? 'none'
          : financial_details.tax.countries_of_citizenship[0],
        anotherCountry:
          financial_details.tax.countries_of_citizenship.length > 1
            ? financial_details.tax.countries_of_citizenship[1]
            : null,
        UKCitizenship: financial_details.tax.uk_citizen
      }
    },
    EmploymentDetailsDIPForm: {
      values: {
        employersName: current_employment_details?.employer?.name,
        /**
         * @todo we want to remove this in the futue
         * @link {UL-5439}
         */
        employmentStartDate: current_employment_details?.effective_period?.from,
        /**
         * @note
         * ~~We've removed employment status (e.g "FULL TIME", "PART TIME")
         * from the forms, so attempting to retrieve this data will lead to a crash.~~
         * @note Turns out we HAVEN'T deleted employment status.
         * But getting rid of it will result in employment data being mapped to `null`...
         * @see customerDataSelector
         */
        employmentStatus: otherEmploymentValues.includes(current_employment_details?.status)
          ? 'OTHER'
          : current_employment_details?.status,
        otherEmploymentStatus: otherEmploymentValues.includes(current_employment_details?.status)
          ? current_employment_details?.status
          : null,
        financialCircumstancesChange: newIncome ? 'YES' : 'NO',
        monthlyIncome: financial_details?.income?.regular?.net_amount,
        mortgageExpense: financial_details?.expenditure?.mortgage?.net_amount,
        paymentFrequency: financial_details?.income?.regular?.frequency,
        /**
         * @note occupation is now considered "optional". Existing customers may have it,
         * but we're not asking New to Bank (NTB) customers for it.
         * @note if api submission fails, consider adding this back, but use an empty string for a value""
         * @link {https://enterprisevm.atlassian.net/browse/UL-5438}
         */
        /*occupation: SERVICES,*/
        annualIncomeBeforeTax: financial_details?.income?.gross_annual,
        creditExpense: financial_details?.expenditure?.others?.net_amount,
        incomeLikelyGoDown: newIncome ? 'YES' : 'NO',
        amountGoesInBankAccountPerMonth: newIncome
      }
    }
  };
  dispatch(mapFields(mappedDipFields));
};

/**
 * Store the dipCase response in `state.dipCaseInfo`
 */
export const createDIPCaseSuccess = createAction(
  'dip-case/createDIPCaseSuccess',
  function prepare(response) {
    return { payload: response };
  }
);

/**
 * Handles the response returned from creating a DIP.
 * For NTB customers, we'll fetch an illustration based on returned
 * data if the DIP result is "successful".
 * @param {*} response
 * @param {*} param
 * @returns
 */
export const createDIPCaseSuccessThunk = (response, param) => (dispatch, getState) => {
  /** store the response in state.dipCaseInfo */
  dispatch(createDIPCaseSuccess(response));

  const {
    submitDipCaseResponse: {
      creditDecision,
      details: { amount, apr, term: termInMonths }
    }
  } = response;

  const {
    loanValues: { repaymentDate },
    config: { journey }
  } = getState();

  let { AddressDetailsDIPForm: { values: { MOVE_IN_DATE: { month, year } = {} } = {} } = {} } =
    param.form;
  if (!param.form.AddressDetailsDIPForm) {
    ({
      MOVE_IN_DATE: { month, year }
    } = param.caseDetails.ntbToDipForm.AddressDetailsDIPForm.values);
  }

  const handleNTBGoodCreditDecisionDIP = () => {
    dispatch(showHideLoader(false));
    dispatch(navigateToScreen(DIP_UNLIKELY_RESULT));
  };

  if (
    creditDecision === GOOD ||
    creditDecision === REALLY_GOOD ||
    creditDecision === PRE_APPROVED
  ) {
    /**
     * if we have a "successful" DIP outcome, we'll fetch a new illustration
     * using the amount, apr and term (in months) that the DIP response gives us.
     * @note We only need to do this for New to Bank journeys ("NTB").
     * Existing remains unchanged
     * @see fetchIllustrationBasedOnDIPCase
     */
    creditDecision === GOOD && journey === NTB_JOURNEY
      ? handleNTBGoodCreditDecisionDIP()
      : dispatch(fetchIllustrationBasedOnDIPCase(amount, apr, repaymentDate, termInMonths));
  } else {
    dispatch(showHideLoader(false));
    dispatch(navigateToScreen(CREDIT_DECISION_ROUTES_MAP[creditDecision]));
  }
  /**
   * Regardless of journey type, we always dispatch this.
   * @note Personally, I'm not sure what this is/means, but don't want to rock-the-boat
   * by removing it. - KW 15/04/2024
   */
  dispatch(saveMoveInDateForReprocess(month, year));
};

/**
 * Fetches an illustration for a given loanAmount, apr, startDate and term.
 * Also Accepts `creditDecision` to pass on to a success handler thunk.
 * This `creditDecision can only come from a DIP response`.
 * This is the same kind of request that the "calculator" screen
 */
export const fetchIllustrationBasedOnDIPCase = createAction(
  'dip-case/fetchIllustration',
  function prepare(loanAmount, apr, startDate, termInMonths) {
    return {
      payload: {
        endPoint: '/banks/CB/loan/illustration/calculate/personal',
        method: 'post',
        body: JSON.stringify({
          loan: {
            amount: loanAmount,
            apr,
            repayment_start_date: startDate,
            term: termInMonths
          }
        }),
        isPaasApi: true,
        addHeader: true,
        success: fetchIllustrationBasedOnDIPCaseSuccessThunk,
        failure: responseFailed,
        version: '1.2.0',
        useAuth: false
      }
    };
  }
);

/**
 * Surfaces the initial response of successfully fetching an illustration based on
 * details provided from a successful Decision in Principle.
 * This is captured by the `caseDetails` reducer, resulting in state being updated
 * with the value of the illustration.
 * @see fetchIllustrationBasedOnDIPCaseSuccessThunk
 * @see fetchIllustrationBasedOnDIPCase
 */
export const fetchIllustrationBasedOnDIPCaseSuccess = createAction(
  'dip-case/fetchIllustrationBasedOnDIPCaseSuccess',
  function prepare(response) {
    return { payload: response };
  }
);

const fetchIllustrationBasedOnDIPCaseSuccessThunk =
  ({ illustration }) =>
  (dispatch) => {
    /** store illustration as `dipCaseIllustration` (see caseDetails reducer) */
    dispatch(fetchIllustrationBasedOnDIPCaseSuccess(illustration));

    /** This behaviour was originally in createDIPCaseSuccess */
    // hide the loading spinner
    dispatch(showHideLoader(false));

    /**
     * Navigate the user to the correct screen based on their credit decision
     * @note if the user actually got here, then they should always be taken to
     * the "Great News" screen as their DIP outcome is successful.
     * The `else` block in @see createDIPCaseSuccessThunk will map other outcomes to the correct screen.
     */
    dispatch(navigateToScreen(DIP_GREAT_NEWS));
  };

export const saveMoveInDateForReprocess = createAction(
  'dip/reprocessMoveInDate',
  function prepare(month, year) {
    return {
      payload: `${month}${year}`
    };
  }
);

export const createDIPCaseFailed =
  ({ error = {} }) =>
  (dispatch) => {
    const { originalError: { response: { data = {} } } = { response: { data: {} } } } = error;
    dispatch(showHideLoader(false));
    dispatch(responseFailed(data));
  };

export const updateCase = createAction(
  'case/update',
  function prepare(
    config,
    loanValues,
    form,
    caseDetails,
    customerInfo,
    caseId,
    path = null,
    isSubmit,
    image,
    account = null,
    newIncome,
    financialCircumstanceValue
  ) {
    return {
      payload: {
        endPoint: `banks/:bankId/cases/personal-loan/${caseId}`,
        method: 'put',
        body: updateCaseRequest(
          config,
          loanValues,
          form,
          caseDetails,
          customerInfo,
          isSubmit,
          account,
          newIncome,
          financialCircumstanceValue
        ),
        isPaasApi: true,
        addHeader: true,
        success: updateCaseSuccess,
        failure: updateCaseFailed,
        successParam: {
          config,
          loanValues,
          form,
          caseDetails,
          customerInfo,
          path,
          isSubmit,
          image
        },
        version: '1.2.0',
        useEnvTag: true
      }
    };
  }
);

export const submitKeyFeaturesNTBSuccessThunk = () => (dispatch, getState) => {
  const { config, loanValues, caseDetails, customer, form } = getState();
  takeScreenShot((image) => {
    dispatch(
      updateCase(
        config,
        loanValues,
        form,
        caseDetails,
        customer.customerInfo,
        caseDetails.caseId,
        null,
        true,
        image,
        null,
        customer?.newIncome
      )
    );
  });
};

export const updateAndGetCase = createAction(
  'case/updateAndGetCase',
  function prepare(
    config,
    loanValues,
    form,
    caseDetails,
    customerInfo,
    caseId,
    path = null,
    isSubmit,
    image,
    account = null,
    newIncome,
    financialCircumstanceValue
  ) {
    return {
      payload: {
        endPoint: `banks/:bankId/cases/personal-loan/${caseId}`,
        method: 'put',
        body: updateCaseRequest(
          config,
          loanValues,
          form,
          caseDetails,
          customerInfo,
          isSubmit,
          account,
          newIncome,
          financialCircumstanceValue
        ),
        isPaasApi: true,
        addHeader: true,
        success: updateAndGetCaseSuccessThunk,
        failure: updateCaseFailed,
        successParam: {
          config,
          loanValues,
          form,
          caseDetails,
          customerInfo,
          path,
          isSubmit,
          image
        },
        version: '1.2.0',
        useEnvTag: true
      }
    };
  }
);

const updateCaseRequest = (
  config,
  loanValues,
  form,
  caseDetails,
  customerInfo,
  isSubmit,
  account,
  newIncome,
  financialCircumstanceValue
) => {
  const { caseInfo } = caseDetails;
  const { isSecondaryApplicant, loanType, journey } = config;
  const { employment_details_extra } = customerInfo;

  return {
    ...createCaseRequest({
      config,
      isLinkCodeRequired: false,
      includeSourceId: false,
      caseDetails
    }),
    ...(!form.DirectDebitInstructionsForm && !account && getRepaymentDay(loanValues, caseInfo)),
    ...getLoanInfo(loanValues, caseInfo, form, account, isSecondaryApplicant),
    ...getAuditInfo(loanValues.eligibilityTime, caseInfo, isSubmit, config),
    ...getAccountDetails(config, account, loanValues),
    ...getCreditCardInfo(form, caseInfo),
    ...getEmploymentInfo(form, config, employment_details_extra),
    ...getUserNameInfo(form, customerInfo, caseInfo),
    ...((form.DirectDebitInstructionsForm ||
      (caseInfo && caseInfo.direct_debit.source_account.account_name)) &&
      getDirectDebitInfo(form, caseDetails, loanValues, customerInfo, account ? account : null)),
    ...getFinancialInfo(form, caseInfo, config, newIncome, financialCircumstanceValue)
  };
};

function getRepaymentDay(loanValues, caseInfo) {
  return {
    repayment: {
      payment_due_day: padZero(
        (loanValues && (loanValues.day || loanValues.repaymentDay)) ||
          caseInfo.repayment.payment_due_day
      )
    }
  };
}

const getLoanInfo = (loanValues, caseInfo, form, account, isSecondaryApplicant) => {
  const { LoanOptionsForm, CreateLoanForm } = form;
  const purpose =
    (LoanOptionsForm && LoanOptionsForm.values && LoanOptionsForm.values.loanPurpose) ||
    loanValues.purpose ||
    (caseInfo && caseInfo.loan && caseInfo.loan.purpose);

  if (isSecondaryApplicant || !purpose) {
    return null;
  }

  if (
    purpose === DEBT_CONSOLIDATION ||
    (LoanOptionsForm &&
      LoanOptionsForm.values &&
      LoanOptionsForm.values.debtConsolidation === 'yes') ||
    loanValues.debtConsolidate === 'yes' ||
    (caseInfo && caseInfo.loan && caseInfo.loan.debt_details)
  ) {
    const debtDetails = getDebtDetails(form, account, loanValues, caseInfo);

    return {
      loan: {
        amount: loanValues.amount || parseFloat(caseInfo.loan.amount),
        term:
          parseInt(loanValues.term) ||
          parseInt(caseInfo?.loan?.term) ||
          parseInt(CreateLoanForm?.values?.loanTerm),
        purpose:
          (LoanOptionsForm && LoanOptionsForm.values && LoanOptionsForm.values.loanPurpose) ||
          loanValues.purpose ||
          caseInfo.loan.purpose,
        ...(debtDetails && { debt_details: debtDetails })
      }
    };
  } else {
    return {
      loan: {
        amount: loanValues.amount || parseFloat(caseInfo.loan.amount),
        term:
          parseInt(loanValues.term) ||
          parseInt(caseInfo?.loan?.term) ||
          parseInt(CreateLoanForm?.values?.loanTerm),
        purpose:
          loanValues.purpose ||
          (LoanOptionsForm && LoanOptionsForm.values && LoanOptionsForm.values.loanPurpose) ||
          caseInfo.loan.purpose
      }
    };
  }
};

const getDebtDetails = (form, account, loanValues, caseInfo) => {
  const { DebtConsolidationForm, InternalDebtConsolidationForm } = form;
  //if debt details is available in case Info and Debt consolidation form is null
  if (
    caseInfo &&
    caseInfo.loan &&
    caseInfo.loan.debt_details &&
    caseInfo.case_subtype !== NTB_JOURNEY &&
    (!DebtConsolidationForm || !InternalDebtConsolidationForm)
  ) {
    let isValidDebtDetails = false;
    //external type has accouint details fin Get case, which needs to avoid in Update case
    const debtDetails = caseInfo.loan.debt_details.map((debt) => {
      const { type, amount, account_details } = debt;
      //get case have debtDetails array but values are empty.
      if (type && amount) {
        isValidDebtDetails = true;
        if (debt.type === EXTERNAL_TYPE) {
          return {
            type: EXTERNAL_TYPE,
            amount: parseFloat(debt.amount)
          };
        }
        const { account_id, account_number, sort_code } = account_details;
        return {
          amount: parseFloat(debt.amount),
          type: debt.type,
          account_details: {
            account_id,
            account_number,
            sort_code
          }
        };
      } else {
        return null;
      }
    });

    return isValidDebtDetails ? debtDetails : null;
  }

  const hasCaseInfoLoanDebtDetails = caseInfo && caseInfo.loan && caseInfo.loan.debt_details;
  if (
    (DebtConsolidationForm && DebtConsolidationForm.values) ||
    loanValues.amountConsolidate ||
    hasCaseInfoLoanDebtDetails
  ) {
    let debtAmount =
      (DebtConsolidationForm &&
        DebtConsolidationForm.values &&
        DebtConsolidationForm.values.amountConsolidation) ||
      loanValues.amountConsolidate;

    if (hasCaseInfoLoanDebtDetails) {
      caseInfo.loan.debt_details.forEach((debt) => {
        const { type, amount } = debt;
        //get case have debtDetails array but values are empty.
        if (amount && type === EXTERNAL_TYPE) {
          debtAmount = parseFloat(debt.amount);
        }
      });
    }

    return debtAmount
      ? [
          {
            amount: parseFloat(debtAmount),
            type: EXTERNAL_TYPE
          }
        ]
      : null;
  }

  if (InternalDebtConsolidationForm) {
    const { values } = InternalDebtConsolidationForm;
    const { accountDetailList } = account;
    const request = [];
    //iterate the values object to create DebtDetails object for Update product
    Object.entries(values).forEach(([key, value]) => {
      let foundAccount = accountDetailList.find((account) => account.id === key);
      if (foundAccount && value && value !== -1) {
        if (foundAccount.type === LOAN_TYPE || foundAccount.type === CURRENT_TYPE) {
          request.push({
            account_details: {
              account_id: foundAccount.id,
              account_number: foundAccount.number.split('-')[1],
              sort_code: foundAccount.number.split('-')[0]
            },
            amount: Math.abs(value),
            type: foundAccount.type
          });
        } else if (foundAccount.type === CREDIT_CARD_TYPE) {
          request.push({
            account_details: {
              account_id: foundAccount.id
            },
            amount: Math.abs(value),
            type: 'creditcard'
          });
        }
      } else if (key === EXTERNAL_TYPE && values.externalAmount && values.externalAmount !== -1) {
        request.push({
          amount: parseFloat(values.externalAmount),
          type: EXTERNAL_TYPE
        });
      }
    });
    return request;
  }
};

const getAuditInfo = (eligibilityTime, caseInfo, isSubmit, config) => {
  if (config.journey !== NTB_JOURNEY) {
    return {
      audit_info: {
        documents: [getDataProtectionDoc(), getKeyFeatureDoc()]
      }
    };
  }
  if (caseInfo) {
    const today = new Date().toISOString();
    const documents = [...caseInfo.audit_info.documents];
    if (isSubmit) documents.push(getKeyFeatureDoc());
    return {
      audit_info: {
        documents: documents,
        eligibility_questions: getEligibilityQuestion(eligibilityTime || today)
      }
    };
  } else {
    return {
      audit_info: {
        documents: [getDataProtectionDoc()],
        eligibility_questions: getEligibilityQuestion(eligibilityTime)
      }
    };
  }
};

const getKeyFeatureDoc = () => {
  return {
    consent: 'Y',
    content_type: 'PDF',
    name: 'Key Features.pdf',
    reason: 'documentation',
    timestamp: new Date().toISOString(),
    version: 'H1'
  };
};

const getDataProtectionDoc = () => {
  return {
    reason: 'data protection',
    name: 'DP.pdf',
    content_type: 'PDF',
    version: 'H1',
    consent: 'Y',
    timestamp: new Date().toISOString()
  };
};

const getCreditCardInfo = (form, caseInfo) => {
  if (
    (!form.CreditCardDetails || !form.CreditCardDetails.values) &&
    (!caseInfo || !caseInfo.card_details.external_credit_card)
  ) {
    return null;
  }
  return {
    card_details: {
      external_credit_card: form.CreditCardDetails
        ? form.CreditCardDetails.values.numberOfCreditCards
        : caseInfo.card_details.external_credit_card
    }
  };
};

const getUserNameInfo = (form, customerInfo, caseInfo) => {
  if (
    (!form.ChooseUserNameForm || !form.ChooseUserNameForm.values) &&
    (!customerInfo || !customerInfo.contact_details) &&
    (!caseInfo || !caseInfo.applicants.user_details.username)
  ) {
    return null;
  }
  let primaryUserName = '';
  if (form.ChooseUserNameForm) {
    const {
      values: { userName }
    } = form.ChooseUserNameForm;
    primaryUserName = userName;
  } else {
    primaryUserName = caseInfo?.applicants?.user_details?.username;
  }
  return {
    applicants: {
      user_details: {
        username: primaryUserName
      }
    }
  };
};

const getEmploymentInfo = (form, config, employment_details_extra) => {
  if (!config.skipDecisionInPrinciple) {
    const {
      EmploymentDetailsDIPForm: {
        values: { occupation, yearField, monthField, otherEmploymentStatus, employmentStatus }
      }
    } = form;
    const employmentInfo = {};
    if (
      otherEmploymentStatus === 'SELF EMPLOYED' ||
      EMPLOYMENT_STATUSES.includes(employmentStatus)
    ) {
      employmentInfo.occupation = occupation;
    }
    if (yearField || monthField) {
      employmentInfo.time_with_prev_emp = `${padZero(yearField)}${padZero(monthField)}`;
    }
    return { employment_details: { ...employmentInfo, ...employment_details_extra } };
  } else if (employment_details_extra) {
    return { employment_details: { ...employment_details_extra } };
  }
};

const getDirectDebitInfo = (form, caseDetails, loanValues, customerInfo, accounts) => {
  const accountList = accounts?.accountList;
  const currentAccountList = accountList && getValidCurrentAccounts(accountList);
  let sortCode = '',
    primaryBankAccount = null;
  if (form.YourMainBankAccountForm) {
    const { values } = form.YourMainBankAccountForm;
    if (values.accountWithUs === YES_VALUE) {
      sortCode = values.mainBankAccount.number.split('-')[0];
    } else {
      const { sortCode1, sortCode2, sortCode3 } = values;
      sortCode = `${sortCode1}${sortCode2}${sortCode3}`;
    }
  } else if (currentAccountList?.length) {
    sortCode = currentAccountList[0].number.split('-')[0];
  } else {
    sortCode = caseDetails.caseInfo.primary_bank_account.account_details.sort_code;
    primaryBankAccount = caseDetails.caseInfo.primary_bank_account;
  }

  const mainBankAccount =
    (form.YourMainBankAccountForm || primaryBankAccount) &&
    getBankAccount(form, sortCode, primaryBankAccount);
  let bankName = null,
    bankAddress = {};
  if (caseDetails.bankInfo) {
    const {
      bankInfo: {
        bank: { name, address }
      }
    } = caseDetails;
    bankName = name;
    bankAddress = address;
  } else {
    const {
      direct_debit: {
        source_account: {
          bank: { name, address }
        }
      }
    } = caseDetails.caseInfo;
    bankName = name;
    bankAddress = {
      address_line1: address.address_line_1,
      address_line2: address.address_line_2,
      address_line3: address.address_line_3,
      address_line4: address.address_line_4,
      city: address.city,
      postal_code: address.postal_code
    };
  }

  let accountNumber = null,
    accountHolderName;

  //get account name and number from form values
  if (form.YourMainBankAccountForm) {
    const { values } = form.YourMainBankAccountForm;
    //if main bank account selected as internal account
    if (values.accountWithUs === YES_VALUE) {
      const {
        personal_details: { name }
      } = customerInfo;

      accountNumber = values.mainBankAccount.number.split('-')[1];
      accountHolderName = `${name.title} ${name.first_name} ${name.middle_name || ''} ${
        name.last_name
      }`;
    } else {
      accountNumber = values.accountNumber;
      accountHolderName = values.accountHolderName;
    }
  } else if (currentAccountList?.length) {
    const {
      number,
      metadata: { account_name }
    } = currentAccountList[0];
    accountNumber = number.split('-')[1];
    accountHolderName = account_name;
  } else {
    const {
      account_details: { account_number, account_name }
    } = primaryBankAccount;

    accountNumber = account_number;
    accountHolderName = account_name;
  }

  const currentAccount = currentAccountList?.length && {
    account_details: {
      account_id: currentAccountList[0]?.id
    }
  };

  return {
    primary_bank_account: mainBankAccount || (currentAccount && currentAccount),
    repayment: {
      payment_due_day: padZero(
        caseDetails.caseInfo &&
          caseDetails.caseInfo.repayment &&
          caseDetails.caseInfo.repayment.payment_due_day !== '0'
          ? caseDetails.caseInfo.repayment.payment_due_day
          : loanValues.repaymentDay
      ),
      ...(mainBankAccount || (currentAccount && currentAccount))
    },
    drawdown: {
      ...(mainBankAccount || (currentAccount && currentAccount))
    },
    direct_debit: {
      source_account: {
        sort_code: sortCode,
        account_number: accountNumber,
        account_name: accountHolderName,
        bank: {
          name: bankName,
          address: {
            address_line_1: bankAddress.address_line1,
            address_line_2: bankAddress.address_line2,
            address_line_3: bankAddress.address_line3,
            address_line_4: bankAddress.address_line4,
            city: bankAddress.city,
            postal_code: bankAddress.postal_code
          }
        }
      }
    }
  };
};

const getBankAccount = (form, sortCode, primaryBankAccount) => {
  if (form.YourMainBankAccountForm) {
    const { accountHolderName, accountNumber, accountWithUs, mainBankAccount } =
      form.YourMainBankAccountForm.values;
    if (accountWithUs === YES_VALUE) {
      return {
        account_details: {
          account_id: mainBankAccount.id
        }
      };
    }
    const { years, months } = form.YourMainBankAccountForm.values.yearMonthAccount;
    return {
      account_details: {
        account_name: accountHolderName,
        sort_code: sortCode,
        account_number: accountNumber,
        time_with_bank: {
          years: years.toString(),
          months: months.toString()
        }
      }
    };
  } else {
    const { account_details } = primaryBankAccount;
    if (account_details.account_id) {
      return {
        account_details: {
          account_id: account_details.account_id
        }
      };
    }
    return {
      account_details: {
        account_name: account_details.account_name,
        sort_code: sortCode,
        account_number: account_details.account_number,
        time_with_bank: account_details.time_with_bank
      }
    };
  }
};

const getEligibilityQuestion = (eligibilityTime) => {
  return [
    {
      question: 'Are you over 18?',
      answer: 'Y',
      consent: 'Y',
      timestamp: eligibilityTime
    },
    {
      question: 'Do you live in UK?',
      answer: 'Y',
      consent: 'Y',
      timestamp: eligibilityTime
    },
    {
      question: 'Are you currently bankrupt?',
      answer: 'N',
      consent: 'N',
      timestamp: eligibilityTime
    },
    {
      question: 'Ever had a CCJ or decree in the last three years?',
      answer: 'N',
      consent: 'N',
      timestamp: eligibilityTime
    }
  ];
};

const getFinancialInfo = (form, caseInfo, config, newIncome, financialCircumstanceValue) => {
  if (config.skipDecisionInPrinciple) {
    if (financialCircumstanceValue === 'No') {
      return null;
    }
    if (!newIncome && !caseInfo?.financial_details?.change_of_circumstance?.income?.gross_annual) {
      return null;
    } else {
      return {
        financial_details: {
          change_of_circumstance: {
            income: {
              gross_annual:
                newIncome ||
                caseInfo?.financial_details?.change_of_circumstance?.income?.gross_annual
                  ? parseInt(
                      newIncome ||
                        caseInfo?.financial_details?.change_of_circumstance?.income?.gross_annual
                    )
                  : 0,
              regular: {
                net_amount: 0,
                input_type: 'direct debit',
                frequency: 'monthly'
              }
            }
          }
        }
      };
    }
  } else {
    const {
      EmploymentDetailsDIPForm: {
        values: { financialCircumstancesChange }
      }
    } = form;
    if (financialCircumstancesChange === 'NO') {
      return null;
    }
    return {
      financial_details: {
        change_of_circumstance: {
          ...getDIPIncomeInfo(form),
          ...getDIPExpenseInfo(form)
        }
      }
    };
  }
};

function getDIPIncomeInfo(form) {
  const {
    EmploymentDetailsDIPForm: {
      values: {
        financialCircumstancesChange,
        incomeLikelyGoDown,
        earningsWhenIncomeDown,
        amountGoesInBankAccountPerMonth
      }
    }
  } = form;
  if (financialCircumstancesChange === 'NO' || incomeLikelyGoDown === 'NO') {
    return null;
  } else if (financialCircumstancesChange === 'YES' && incomeLikelyGoDown === 'YES') {
    return {
      income: {
        gross_annual: parseInt(earningsWhenIncomeDown),
        regular: {
          net_amount: parseInt(amountGoesInBankAccountPerMonth),
          input_type: 'direct debit',
          frequency: 'monthly'
        }
      }
    };
  }
}

function getDIPExpenseInfo(form) {
  const {
    EmploymentDetailsDIPForm: {
      values: {
        financialCircumstancesChange,
        outgoingsLikelyGoUp,
        monthlyMortgagePayout,
        monthlyPersonalLoansPayout
      }
    }
  } = form;
  if (financialCircumstancesChange === 'NO' || outgoingsLikelyGoUp === 'NO') {
    return null;
  } else if (financialCircumstancesChange === 'YES' && outgoingsLikelyGoUp === 'YES') {
    return {
      expenditure: {
        mortgage: {
          net_amount: parseInt(monthlyMortgagePayout),
          frequency: 'monthly'
        },
        others: {
          net_amount: parseInt(monthlyPersonalLoansPayout),
          frequency: 'monthly'
        }
      }
    };
  }
}

const getAccountDetails = (config, account, loanValues) => {
  if (config.journey === PRE_ASSESSED_JOURNEY) {
    return {
      drawdown: {
        account_details: {
          account_id: account.drawDownAccountId
        }
      },
      repayment: {
        account_details: {
          account_id: account.repaymentAccountId
        },
        payment_due_day: padZero(loanValues.repaymentDay)
      }
    };
  }
  return null;
};
export const updateAndGetCaseSuccessThunk = (response) => (dispatch, getState) => {
  dispatch(updateAndGetCaseSuccessResponse(response));
  const {
    caseDetails: { caseId }
  } = getState();
  dispatch(getCase(caseId, false, true, false, false)); //true to redirect to next screen
};

export const updateCaseSuccess = (response, param) => (dispatch, getState) => {
  dispatch(updateCaseSuccessResponse(response));
  if (param) {
    const { config, path, caseDetails, isSubmit, image } = param;
    if (config.journey !== PRE_ASSESSED_JOURNEY) {
      if (path) {
        dispatch(showHideLoader(false));
        dispatch(
          config.skipDecisionInPrinciple
            ? navigateToScreen(path)
            : navigateToScreen(DIP_INFORMATION_ABOUT_YOUR_LOAN)
        );
      }

      const { fingerprint } = getState().caseDetails;
      isSubmit && dispatch(submitCase(config, caseDetails, image, fingerprint));
    } else {
      dispatch(getNextTask(caseDetails.caseId));
    }
  }
};

export const updateCaseSuccessResponse = createAction(
  'case/updateCaseSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const updateAndGetCaseSuccessResponse = createAction(
  'case/updateAndGetCaseSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const resetUpdateStatus = createAction('case/resetUpdateStatus');

export const updateCaseFailed = (failedResponse) => (dispatch) => {
  const { error, data } = failedResponse;

  if (
    (data && data.error && data.error.code === '1036') ||
    error?.originalError?.response?.data?.error?.code === '1036'
  ) {
    dispatch(showUserNameExists(true));
    dispatch(showHideLoader(false));
    if (
      window.location.pathname === NTB_DIP_KEY_FEATURES ||
      window.location.pathname === DIP_INFORMATION_ABOUT_YOUR_LOAN
    ) {
      dispatch(responseFailed(error));
    }
  } else {
    dispatch(responseFailed(error));
  }
};

export const showUserNameExists = createAction(
  'case/showUserNameExists',
  function prepare(userNameExist) {
    return {
      payload: {
        showUserNameExists: userNameExist
      }
    };
  }
);

export const getCase = createAction(
  'case/getCase',
  function prepare(
    caseId,
    callNextTask = false,
    redirectNextScreen = false,
    shouldNavigateToDIPOnGetCaseSuccess = false,
    shouldUpdateEmployment = true
  ) {
    return {
      payload: {
        endPoint: `banks/:bankId/cases/personal-loan/${caseId}`,
        method: 'get',
        isPaasApi: true,
        addHeader: true,
        success: getCaseSuccess,
        successParam: {
          caseId,
          callNextTask,
          redirectNextScreen,
          shouldNavigateToDIPOnGetCaseSuccess,
          shouldUpdateEmployment
        },
        failure: responseFailed,
        version: '1.2.0',
        useEnvTag: true
      }
    };
  }
);

export const getCaseSuccess = (response, param) => (dispatch, getState) => {
  dispatch(getCaseSuccessResponse(response));
  const {
    case_subtype,
    product: { sub_type },
    joint_link_code,
    employment_details: {
      employment_job_level: employmentJobLevel,
      employment_industry: employmentIndustry
    }
  } = response;

  const {
    callNextTask,
    caseId,
    redirectNextScreen,
    shouldNavigateToDIPOnGetCaseSuccess,
    shouldUpdateEmployment
  } = param;

  if ((employmentJobLevel || employmentIndustry) && shouldUpdateEmployment) {
    dispatch(addEmploymentDetails({ employmentJobLevel, employmentIndustry }));
  }

  const {
    account: { accountList }
  } = getState();

  //set user journey based on get case subtype,i.e ntb | pre-assessed | existing
  /**
   * @note if the `case_subtype` is "ntb" - prioritise this as this will enforce keeping the
   * customer on the NTB Journey screens.
   * @deprecated Joint cases are no longer supported. This may be needlessly complex.
   */
  dispatch(
    setUserJourney(
      sub_type === JOINT_TYPE || case_subtype === NTB_JOURNEY ? case_subtype : sub_type
    )
  );
  /**
   * @deprecated Joint cases are no longer supported. This may be needlessly complex.
   */
  dispatch(setSecondaryApplicant(!!joint_link_code));
  /**
   * Update the joint flag to identify the sole or joint journey
   * @deprecated Joint cases are no longer supported. This may be needlessly complex.
   */
  dispatch(setJointJourney(sub_type === JOINT_TYPE));

  if (!callNextTask) {
    dispatch(
      case_subtype !== NTB_JOURNEY && accountList.length === 0
        ? getAccountList()
        : showHideLoader(false)
    );

    // This flag is check if get case done from Confirm link code screen and navigate to respective screen
    /**
     *  We additionally guard against misuse of the flag so that `redirectToNextScreen` doesn't overrule `navigateToDIP`
     */
    if (redirectNextScreen && shouldNavigateToDIPOnGetCaseSuccess === false) {
      dispatch(
        navigateToScreen(case_subtype === NTB_JOURNEY ? CHOOSE_A_USERNAME : YOUR_MAIN_BANK_ACCOUNT)
      );
    }

    if (shouldNavigateToDIPOnGetCaseSuccess) {
      /**
       * No more network requests to make.
       * Hide the loader and navigate to the "About this (elegibility) check" screen.
       */
      dispatch(navigateToScreen(DIP_ABOUT_THIS_CHECK));
    }
  } else {
    dispatch(
      getNextTask(
        caseId,
        false,
        sub_type === JOINT_TYPE || case_subtype === NTB_JOURNEY ? case_subtype : sub_type
      )
    );
  }
};

export const getCaseSuccessResponse = createAction(
  'case/getCaseSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

/**
 * Makes a new Challenge request with the current caseId
 * @returns
 */
export const getChallenge = () => (dispatch, getState) => {
  const {
    caseDetails: { caseId }
  } = getState();
  dispatch(showHideLoader(true));
  dispatch(requestChallenge(caseId));
};

export const getMitekResult = () => (dispatch, getState) => {
  const {
    caseDetails: { caseId }
  } = getState();
  dispatch(showHideLoader(true));
  dispatch(setMitekOutcomeResponse(true));
  dispatch(getNextTask(caseId));
};

export const requestChallenge = createAction('case/makeChallenge', function prepare(caseId) {
  //this preps the backend for the subsequent getNext call, which will return the Mitek security question url
  return {
    payload: {
      endPoint: `banks/:bankId/cases/personal-loan/${caseId}/verify/challenge`,
      method: 'get',
      isPaasApi: true,
      addHeader: true,
      success: getChallengeSuccess,
      failure: getChallengeFailed,
      version: '1.2.0',
      useEnvTag: true
    }
  };
});

export const getChallengeSuccess = () => (dispatch, getState) => {
  const {
    caseDetails: { caseId }
  } = getState();
  dispatch(setChallengeRequestResponse(true));
  dispatch(getNextTask(caseId));
};

const getChallengeFailed = () => (dispatch) => {
  dispatch(setChallengeRequestResponse(true));
  dispatch(showHideLoader(false));
};

export const setChallengeRequestResponse = createAction(
  'case/setChallengeRequestResponse',
  function prepare(isChallengeRequested) {
    return {
      payload: isChallengeRequested
    };
  }
);

export const setMitekOutcomeResponse = createAction(
  'case/setMitekOutcomeResponse',
  function prepare(isOutcomeReceived) {
    return {
      payload: isOutcomeReceived
    };
  }
);

export const submitCase = createAction(
  'case/submitCase',
  function prepare(config, caseDetails, image) {
    return {
      payload: {
        endPoint: `banks/:bankId/cases/personal-loan/${caseDetails.caseId}/submit`,
        method: 'put',
        body: {
          case_subtype: config.journey !== NTB_JOURNEY ? EXISTING_JOURNEY : NTB_JOURNEY,
          device_details: {
            blackbox: window.Cypress ? 'CYPRESS_BLACKBOX_DEFAULT_VALUE' : getBlackbox()
          },
          ...(config.branchSortCode && { sort_code: config.branchSortCode })
        },
        isPaasApi: true,
        addHeader: true,
        success: submitCaseSuccess,
        successParam: {
          caseDetails,
          image
        },
        failure: responseFailed,
        version: '1.2.0'
      }
    };
  }
);

export const submitCaseSuccess = (response, param) => (dispatch) => {
  dispatch(submitCaseSuccessResponse(response));

  dispatch(submitEvidence(param.caseDetails, param.image));
};

export const submitCaseSuccessResponse = createAction(
  'case/submitCaseSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const submitEvidence = createAction(
  'case/submitEvidence',
  function prepare(caseDetails, image, isDecision = false, path = null) {
    return {
      payload: {
        endPoint: `banks/:bankId/cases/personal-loan/${caseDetails.caseId}/evidence`,
        method: 'post',
        body: buildEvidenceRequestBody(image),
        isPaasApi: true,
        addHeader: true,
        success: submitEvidenceSuccess,
        successParam: {
          caseId: caseDetails.caseId,
          isDecision,
          path
        },
        failure: responseFailed,
        version: '1.2.0',
        useEnvTag: true
      }
    };
  }
);

function buildEvidenceRequestBody(image) {
  const date = new Date();
  return {
    context: 'personal loan app',
    capture_timestamp: date.toISOString().substring(0, date.toISOString().lastIndexOf('.')), // format ISO date without milliseconds component (or Z at end)
    image: {
      mime_type: 'image/jpeg',
      data: [image]
    }
  };
}

export const submitEvidenceSuccess = (response, param) => (dispatch) => {
  dispatch(submitEvidenceSuccessResponse(response));
  const { caseId, isDecision, path } = param;
  if (!isDecision) {
    dispatch(getNextTask(caseId));
  } else if (path) {
    dispatch(showHideLoader(false));
    dispatch(redirectCustomerDetails(path));
  }
};

export const submitEvidenceSuccessResponse = createAction(
  'case/submitEvidenceSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const getNextTask = createAction(
  'case/getNextTask',
  function prepare(caseId, showLoader = false, subType = null) {
    return {
      payload: {
        endPoint: `banks/:bankId/cases/personal-loan/${caseId}/next`,
        method: 'get',
        isPaasApi: true,
        addHeader: true,
        success: getNextTaskSuccess,
        successParam: { showLoader, subType, caseId },
        failure: responseFailed,
        version: '1.2.0',
        useEnvTag: true
      }
    };
  }
);

export const getNextTaskSuccess = (response, param, status) => (dispatch, getState) => {
  if (response.TaskInfoGroup) {
    const { showLoader, subType, caseId } = param;
    const {
      TaskInfoGroup: {
        action: { uri },
        id
      }
    } = response;
    const task = nextRouteTask(caseId, uri, id);
    const {
      config: { jointLinkCode, loanType, origin, journey },
      caseDetails: { caseSubmitted }
    } = getState();

    let allowExistingJourney = false;
    const { expected_wait_time_sec = '0' } = response.TaskInfoGroup;
    if (status === STATUS_CODE_ACCEPTED) {
      setTimeout(
        () => dispatch(getNextTask(caseId)),
        parseInt(expected_wait_time_sec) * A_SECOND_IN_MS
      );
    } else {
      //if journey not NTB then call API , based on route path condition i.e if informed choice call pre assessed and account list
      if (subType && subType !== NTB_JOURNEY) {
        const isNewJourney = startJourneyAgain(task, caseSubmitted);
        //if case is existing and route path is to navigate to inform choice as per old case status.
        //call pre assessed API to get the customer pre assessed info
        allowExistingJourney = (task && task.routePath[subType] === INFORM_CHOICE) || isNewJourney;
      } else {
        const { isNative } = getDeviceContext();
        //start journey as existing customer if NTB customer starting journey and last case status is account opened
        //or status is decline|expired|cancelled and starting from IB/Mobile
        if (
          task &&
          !caseSubmitted &&
          (task.id === TASK_ACCOUNT_OPENED ||
            ((isNative || origin === ORIGIN_IB) &&
              (task.id === TASK_CREDIT_DECLINE ||
                task.id === TASK_APPLICATION_EXPIRED ||
                task.id === TASK_APPLICATION_CANCELLED)))
        ) {
          allowExistingJourney = true;
        }
      }
      if (allowExistingJourney) {
        dispatch(startExistingJourney(jointLinkCode, loanType));
      } else {
        if (loanType === RESUME_LOAN_TYPE && journey === EXISTING_JOURNEY) {
          dispatch(navigateToScreen(WARNING_MESSAGE_SCREEN));
        }
        dispatch(getNextTaskSuccessResponse(response));
        dispatch(showHideLoader(showLoader));
      }
    }
  } else dispatch(responseFailed(response));
};

export const getNextTaskSuccessResponse = createAction(
  'case/getNextTaskSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

/**
 * start journey if case is decline or account opened and resume the case or start journey again
 */
const startJourneyAgain = (task, caseSubmitted) => {
  //check whether case status is decline or account opened and flow is resume flow then call next API to start the journey again
  if (
    task &&
    (task.id === TASK_CREDIT_DECLINE || task.id === TASK_ACCOUNT_OPENED) &&
    !caseSubmitted
  ) {
    return true;
  }
  return false;
};

/**
 * this will dispatch the pre assessed info action to get pre assessed info and start journey accordingly
 * Reset existing case details
 */
const startExistingJourney = (jointLinkCode, loanType) => (dispatch, getState) => {
  const {
    config: { skipDecisionInPrinciple }
  } = getState();
  dispatch(setSecondaryApplicant(!!jointLinkCode));
  dispatch(setJointJourney(loanType === JOINT_TYPE));
  dispatch(setUserJourney(EXISTING_JOURNEY));
  skipDecisionInPrinciple
    ? dispatch(push(WARNING_MESSAGE_SCREEN))
    : dispatch(push(DIP_YOUR_LOAN_OPTIONS));
  dispatch(getAccountList());
  //reset case details as journey is restart and not require old case info
  dispatch(resetCaseInfo());
  dispatch(setCaseCreatedDate(null));
};

export const offerDecision = createAction(
  'case/offerDecision',
  function prepare(
    caseId,
    preContractName,
    loanAgreementName,
    offerId,
    journey = NTB_JOURNEY,
    isJoint = false
  ) {
    return {
      payload: {
        endPoint: `/banks/:bankId/cases/personal-loan/${caseId}/offer/decision`,
        method: 'put',
        body: createOfferDecisionBody(preContractName, loanAgreementName, offerId, isJoint),
        isPaasApi: true,
        addHeader: true,
        success: offerDecisionSuccess,
        successParam: {
          caseId,
          journey
        },
        failure: responseFailed,
        version: '1.2.0',
        useEnvTag: true
      }
    };
  }
);

const createOfferDecisionBody = (preContractName, loanAgreementName, offerId, isJoint) => {
  const datenow = new Date().toISOString();
  return {
    offer: [
      {
        id: offerId,
        decision: isJoint ? 'post' : 'accept' // joint appication will have 'post'decision text
      }
    ],
    audit_info: {
      documents: [
        {
          reason: 'data protection',
          name: 'DP.pdf',
          content_type: 'PDF',
          version: 'H1',
          consent: 'Y',
          timestamp: datenow
        },
        {
          reason: 'documentation',
          name: preContractName,
          content_type: 'PDF',
          version: 'H1',
          consent: 'Y',
          timestamp: datenow
        },
        {
          reason: 'documentation',
          name: loanAgreementName,
          content_type: 'PDF',
          version: 'H1',
          consent: 'Y',
          timestamp: datenow
        }
      ]
    }
  };
};

export const offerDecisionSuccess = (response, param) => (dispatch, getState) => {
  dispatch(offerDecisionSuccessResponse(response));
  const { caseId, journey } = param;
  const {
    config: { isJoint }
  } = getState();
  if (!isJoint) {
    if (journey === NTB_JOURNEY && caseId) {
      dispatch(getNextTask(caseId));
    } else if (journey === PRE_ASSESSED_JOURNEY) {
      dispatch(getCompleteCase(caseId));
    }
  } else {
    dispatch(showHideLoader(false));
    dispatch(navigateToScreen(DOCUMENTS_ON_WAY));
  }
};

export const offerDecisionSuccessResponse = createAction(
  'case/offerDecisionSuccessResponse',
  function prepare(response) {
    return {
      payload: response
    };
  }
);

export const getCaseList = createAction('case/getCaseList', function prepare(isExisting, loanType) {
  return {
    payload: {
      endPoint: `banks/:bankId/cases`,
      method: 'get',
      isPaasApi: true,
      addHeader: true,
      success: getCaseListSuccess,
      successParam: {
        isExisting,
        loanType
      },
      failure: responseFailed,
      version: '0.8.0',
      useEnvTag: false
    }
  };
});

export const getCaseListSuccess = (response, param) => (dispatch, getState) => {
  let callPreAssessed = true;
  const {
    config: { skipDecisionInPrinciple, journey, loanType }
  } = getState();
  /**
   * The response should give us a JSON object with a `cases` array.
   * The response includes _all_ product cases, not just personal loan applications.
   *
   * @note in an ideal world, we'd get the backend to let us request a specific type of case
   * in advance, so that we don't overfetch unrelated products, such as mortgages or account opening.
   */
  if (response.cases && response.cases.length > 0) {
    // dispatch get case

    /**
     * An array of product application cases for the current User.
     * This is filtered to only included Loan Applications (beginning with `LA-`),
     * and sorted to prioritise the most recently updated case, in the rare scenario
     * that there is somehow more-than-one active loan application case.
     *
     * Each case in the array has an `id`, `updated_at` timestamp, and a `created_at` timestamp.
     */
    const cases = response.cases
      .filter((c) => c.id.startsWith('LA-'))
      .sort((a, b) => b.updated_at - a.updated_at);

    if (cases.length > 0) {
      callPreAssessed = false;
      /**
       * @todo
       * Really not sure why, after just filtering and sorting loan cases into a new array,
       * we then take the original `cases` array and use the created_at date for the first one.
       * This may be a logic error that's just been allowed to exist for 4 years.
       * – Kraig Walker, 24/04/2024
       * state.caseDetails.caseCreatedDate
       * @todo investigate what we use this for.
       */
      dispatch(setCaseCreatedDate(response.cases[0].created_at));
      /**
       * Show the loading spinner to the user.
       */
      dispatch(showHideLoader(true));
      /**
       * Set the current caseId in redux state for the duration of the session to the first case
       * in our sorted loansCases array.
       * We consider this to be the "active" or "current" case.
       */
      dispatch(setCaseId(cases[0].id));
      /**
       * Retrieve the full case object and call `getNextTask`.
       */
      dispatch(getCase(cases[0].id, true));
    } else if (!param.isExisting && param.loanType === 'resume') {
      /**
       * If we're not an existing customer, but we've navigated to the app with `resume` in our
       * querystring:
       * @note
       * This is a temporary solution to display warning screen for customers coming directly from brochureware and not from mobile app.
       * This needs to be refactored once permanent solution is finalised.
       * - Avadhut
       */
      dispatch(showHideLoader(false));
      dispatch(navigateToScreen(WARNING_MESSAGE_SCREEN));
    }
  }

  if (param.isExisting && callPreAssessed) {
    dispatch(setUserJourney(EXISTING_JOURNEY));
    skipDecisionInPrinciple
      ? dispatch(push(WARNING_MESSAGE_SCREEN))
      : dispatch(push(DIP_YOUR_LOAN_OPTIONS));
    dispatch(getAccountList(!callPreAssessed));
  }
  if (
    response.cases &&
    !response.cases.length &&
    loanType === RESUME_LOAN_TYPE &&
    journey === NTB_JOURNEY
  ) {
    dispatch(showHideLoader(false));
    dispatch(push(YOUR_LOAN_OPTIONS));
  }
};

export const setCaseCreatedDate = createAction(
  'case/setCaseCreatedDate',
  /**
   *
   * @param {string} createdAtDate
   * @returns
   */
  function prepare(createdAtDate) {
    return {
      payload: createdAtDate
    };
  }
);

export const setCaseId = createAction('case/setCaseId', function prepare(caseId) {
  return {
    payload: caseId
  };
});

export const resetCaseInfo = createAction('case/resetCaseInfo', function prepare() {
  return {
    payload: true
  };
});

export const setMitekUrl = createAction('case/setMitekUrl', function prepare(mitekUrl) {
  return {
    payload: mitekUrl
  };
});

/**
 * This will update the flag for the case status having ID check can not done
 */
export const failedIdentityCheck = createAction('case/failedIdentityCheck');

export const setReduxFormOldData = createAction(
  'dipFormActions/setReduxFormOldData',
  function prepare(formData) {
    return { payload: formData };
  }
);

export const resetReduxFormOldData = createAction('dipFormActions/resetReduxFormOldData');
